<template>
    <div class="p-4 space-y-4">
        <p class="text-sm text-slate-600">
            Match each column from your file to the corresponding field. Required fields are marked with <span
                class="text-red-500">*</span>.
        </p>

        <div class="space-y-3 max-h-80 overflow-y-auto">
            <div v-for="header in headers" :key="header" class="flex items-center space-x-4 p-3 bg-slate-50 rounded-lg">
                <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-slate-700 truncate">{{ header }}</p>
                    <p class="text-xs text-slate-500 truncate">
                        {{ getPreviewValue(header) }}
                    </p>
                </div>
                <UIcon name="i-ion-arrow-forward" class="text-slate-400 flex-shrink-0" />
                <div class="flex-1 min-w-56 max-w-sm">
                    <USelectMenu :model-value="localMapping[header] || ''"
                        @update:model-value="updateMapping(header, $event)" :items="getFieldOptions()"
                        placeholder="Select field..." size="sm" value-key="value" label-key="label"
                        :content="{ side: 'bottom', align: 'start' }" class="w-full" />
                </div>
            </div>
        </div>

        <!-- Unmapped Required Fields Warning -->
        <div v-if="unmappedRequiredFields.length > 0" class="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p class="text-sm text-yellow-800 font-medium">
                Required fields not yet mapped:
            </p>
            <ul class="text-sm text-yellow-700 mt-1 list-disc list-inside">
                <li v-for="field in unmappedRequiredFields" :key="field.key">
                    {{ field.label }}
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">

const props = defineProps<{
    headers: string[];
    fields: FieldDef[];
    previewRows: string[][];
    enableCustomFields?: boolean;
}>();

const mapping = defineModel<Record<string, string>>('mapping', { required: true });

const localMapping = ref<Record<string, string>>({});

// Sync from parent
watch(mapping, (val) => {
    localMapping.value = { ...val };
}, { immediate: true });

function updateMapping(header: string, fieldKey: string) {
    // If this field was already mapped to another header, clear it
    // (skip and custom can be used multiple times)
    for (const h of props.headers) {
        if (h !== header && localMapping.value[h] === fieldKey && fieldKey !== '__skip__' && fieldKey !== '__custom__' && fieldKey !== '') {
            localMapping.value[h] = '';
        }
    }

    localMapping.value[header] = fieldKey;
    mapping.value = { ...localMapping.value };
}

function getFieldOptions() {
    return buildFieldOptions(props.fields, props.enableCustomFields);
}

function isRequired(field: FieldDef): boolean {
    return field.constraints?.some(c => c.type === 'required') ?? false;
}

function getPreviewValue(header: string): string {
    const headerIndex = props.headers.indexOf(header);
    if (headerIndex === -1 || props.previewRows.length === 0) return '(empty)';

    const values = props.previewRows
        .map(row => row[headerIndex])
        .filter(v => v)
        .slice(0, 2);

    return values.length > 0 ? values.join(', ') : '(empty)';
}

const unmappedRequiredFields = computed(() => {
    const mappedKeys = new Set(Object.values(localMapping.value).filter(v => v && v !== '__skip__'));
    return props.fields.filter(f =>
        isRequired(f) && !mappedKeys.has(f.key)
    );
});
</script>
